import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, LogOut, ArrowLeft, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function AdminSuccessStoriesPage() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStory, setEditingStory] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();

  const emptyStory = {
    title: '',
    slug: '',
    client: '',
    logo: '',
    location: '',
    year: new Date().getFullYear().toString(),
    challenge: '',
    solution: '',
    results: [''],
    quote: '',
    quote_attribution: '',
    project_link: '',
    order: 0,
    status: 'published'
  };

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadStories();
  }, [navigate]);

  const loadStories = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/success-stories`);
      const data = await response.json();
      setStories(data);
    } catch (err) {
      toast.error('Failed to load success stories');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (title) => {
    setEditingStory({
      ...editingStory,
      title,
      slug: generateSlug(title)
    });
  };

  const addResult = () => {
    setEditingStory({
      ...editingStory,
      results: [...editingStory.results, '']
    });
  };

  const updateResult = (index, value) => {
    const newResults = [...editingStory.results];
    newResults[index] = value;
    setEditingStory({ ...editingStory, results: newResults });
  };

  const removeResult = (index) => {
    const newResults = editingStory.results.filter((_, i) => i !== index);
    setEditingStory({ ...editingStory, results: newResults });
  };

  const handleSave = async () => {
    if (!editingStory.title || !editingStory.slug) {
      toast.error('Title and slug are required');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const method = isCreating ? 'POST' : 'PUT';
      const url = isCreating 
        ? `${backendUrl}/api/admin/success-stories`
        : `${backendUrl}/api/admin/success-stories/${editingStory.id}`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingStory)
      });

      if (!response.ok) throw new Error('Save failed');

      toast.success(isCreating ? 'Success story created' : 'Success story updated');
      setEditingStory(null);
      setIsCreating(false);
      loadStories();
    } catch (err) {
      toast.error('Failed to save success story');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this success story? This cannot be undone.')) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/success-stories/${id}`, { method: 'DELETE' });
      toast.success('Success story deleted');
      loadStories();
    } catch (err) {
      toast.error('Failed to delete success story');
    }
  };

  const logout = () => {
    sessionStorage.removeItem('admin_auth');
    navigate('/admin');
  };

  if (loading) return <div className="min-h-screen bg-[hsl(var(--bk-bg))] flex items-center justify-center"><p className="text-[hsl(var(--bk-fg))]">Loading...</p></div>;

  // Editing View
  if (editingStory) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => {
                  setEditingStory(null);
                  setIsCreating(false);
                }}
              >
                <ArrowLeft size={16} className="mr-2" /> Back
              </Button>
              <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))]">
                {isCreating ? 'New Success Story' : 'Edit Success Story'}
              </h1>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} className="bg-[hsl(var(--bk-accent))]">
                <Save size={16} className="mr-2" /> Save
              </Button>
            </div>
          </div>

          <div className="space-y-6 bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6">
            {/* Title & Slug */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={editingStory.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Heritage of Craft Campaign"
                  className="bg-[hsl(var(--bk-bg))]"
                />
              </div>
              <div>
                <Label>Slug</Label>
                <Input
                  value={editingStory.slug}
                  onChange={(e) => setEditingStory({ ...editingStory, slug: e.target.value })}
                  placeholder="heritage-of-craft"
                  className="bg-[hsl(var(--bk-bg))]"
                />
              </div>
            </div>

            {/* Client & Location */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Client</Label>
                <Input
                  value={editingStory.client}
                  onChange={(e) => setEditingStory({ ...editingStory, client: e.target.value })}
                  placeholder="Patrón Tequila"
                  className="bg-[hsl(var(--bk-bg))]"
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={editingStory.location}
                  onChange={(e) => setEditingStory({ ...editingStory, location: e.target.value })}
                  placeholder="Jalisco, Mexico"
                  className="bg-[hsl(var(--bk-bg))]"
                />
              </div>
              <div>
                <Label>Year</Label>
                <Input
                  value={editingStory.year}
                  onChange={(e) => setEditingStory({ ...editingStory, year: e.target.value })}
                  placeholder="2024"
                  className="bg-[hsl(var(--bk-bg))]"
                />
              </div>
            </div>

            {/* Logo URL */}
            <div>
              <Label>Client Logo URL (optional)</Label>
              <Input
                value={editingStory.logo}
                onChange={(e) => setEditingStory({ ...editingStory, logo: e.target.value })}
                placeholder="https://..."
                className="bg-[hsl(var(--bk-bg))]"
              />
            </div>

            {/* Challenge */}
            <div>
              <Label>Challenge</Label>
              <Textarea
                value={editingStory.challenge}
                onChange={(e) => setEditingStory({ ...editingStory, challenge: e.target.value })}
                placeholder="Describe the challenge..."
                rows={4}
                className="bg-[hsl(var(--bk-bg))]"
              />
            </div>

            {/* Solution */}
            <div>
              <Label>Solution</Label>
              <Textarea
                value={editingStory.solution}
                onChange={(e) => setEditingStory({ ...editingStory, solution: e.target.value })}
                placeholder="Describe the solution..."
                rows={4}
                className="bg-[hsl(var(--bk-bg))]"
              />
            </div>

            {/* Results */}
            <div>
              <Label className="mb-2 block">Results</Label>
              <div className="space-y-2">
                {editingStory.results.map((result, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={result}
                      onChange={(e) => updateResult(index, e.target.value)}
                      placeholder="40% increase in brand engagement"
                      className="bg-[hsl(var(--bk-bg))]"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeResult(index)}
                      className="text-red-500"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addResult}>
                  <Plus size={14} className="mr-1" /> Add Result
                </Button>
              </div>
            </div>

            {/* Quote */}
            <div>
              <Label>Quote (optional)</Label>
              <Textarea
                value={editingStory.quote}
                onChange={(e) => setEditingStory({ ...editingStory, quote: e.target.value })}
                placeholder="Optional testimonial quote..."
                rows={2}
                className="bg-[hsl(var(--bk-bg))]"
              />
            </div>

            <div>
              <Label>Quote Attribution</Label>
              <Input
                value={editingStory.quote_attribution}
                onChange={(e) => setEditingStory({ ...editingStory, quote_attribution: e.target.value })}
                placeholder="John Doe, Marketing Director"
                className="bg-[hsl(var(--bk-bg))]"
              />
            </div>

            {/* Project Link */}
            <div>
              <Label>Link to Project (optional)</Label>
              <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-2">
                Enter project slug (e.g., "heritage-of-craft"). Leave empty to hide link.
              </p>
              <Input
                value={editingStory.project_link}
                onChange={(e) => setEditingStory({ ...editingStory, project_link: e.target.value })}
                placeholder="heritage-of-craft"
                className="bg-[hsl(var(--bk-bg))]"
              />
            </div>

            {/* Meta */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Order</Label>
                <Input
                  type="number"
                  value={editingStory.order}
                  onChange={(e) => setEditingStory({ ...editingStory, order: parseInt(e.target.value) || 0 })}
                  className="bg-[hsl(var(--bk-bg))]"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={editingStory.status}
                  onValueChange={(value) => setEditingStory({ ...editingStory, status: value })}
                >
                  <SelectTrigger className="bg-[hsl(var(--bk-bg))]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))] mb-2">Success Stories</h1>
            <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">Manage success stories for Mexico page</p>
          </div>
          <div className="flex gap-4">
            <Link to="/admin">
              <Button variant="outline"><ArrowLeft size={16} className="mr-2" /> Back to Admin</Button>
            </Link>
            <Button
              onClick={() => {
                const nextOrder = stories.length > 0 ? Math.max(...stories.map(s => s.order)) + 1 : 0;
                setEditingStory({ ...emptyStory, id: `story-${Date.now()}`, order: nextOrder });
                setIsCreating(true);
              }}
              className="bg-[hsl(var(--bk-accent))]"
            >
              <Plus size={16} className="mr-2" /> New Success Story
            </Button>
            <Button variant="outline" onClick={logout}><LogOut size={16} className="mr-2" /> Logout</Button>
          </div>
        </div>

        <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-[hsl(var(--bk-line))]">
              <tr>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">TITLE</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">CLIENT</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">YEAR</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">STATUS</th>
                <th className="text-right p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {stories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                    No success stories yet. Create your first one!
                  </td>
                </tr>
              ) : (
                stories.map(story => (
                  <tr key={story.id} className="border-b border-[hsl(var(--bk-line))]">
                    <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg))]">{story.title}</td>
                    <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg-muted))]">{story.client}</td>
                    <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg-muted))]">{story.year}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${story.status === 'published' ? 'bg-green-600' : 'bg-yellow-600'}`}>
                        {story.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        {story.project_link && (
                          <Link to={`/work/${story.project_link}`} target="_blank">
                            <Button variant="ghost" size="sm"><Eye size={14} /></Button>
                          </Link>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setEditingStory(story)}>
                          <Edit size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(story.id)}>
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
